// See https://familysearch.slack.com/archives/C06UUFRP6LC/p1715012145722099

import { useEffect, useMemo } from 'react'
import { useUser } from '@fs/zion-user'
import { DateTime } from 'luxon'
import { useQuery } from '@tanstack/react-query'
import { RefetchManager } from '../utils/RefetchManager'
import checkUserSession from '../utils/checkUserSession'
import useSessionChecker from './useSessionChecker'
import axios from '../utils/giveAxios'

export const timezone = (date) => {
  const local = date ? DateTime.local(date) : DateTime.local()
  return encodeURIComponent(local.zoneName)
}

const url = `/service/volunteer/edge/statistics/user/statistics?timezone=${timezone()}`

const refetchManager = new RefetchManager()

export default function useUserStats() {
  const { cisId, signedIn, userLoading } = useUser()
  const { data: sessionData, error: sessionError, loading: sessionLoading } = useSessionChecker()
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['userStats', cisId],
    queryFn: async () => {
      const session = await checkUserSession()
      if (!session?.authenticated) return { stats: { allTime: { activities: {} } } }
      const res = await axios.get(url)
      return res?.data
    },
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
  // we won't need these hooks if we can upgrade to react-query v5. we have to use v4 because we're stuck on React 17.
  useEffect(() => {
    if (signedIn && !userLoading && sessionData?.authenticated && !sessionLoading && !sessionError) {
      refetchManager.startRefetch(refetch)
    }
    return () => {
      refetchManager.clearRefetch()
    }
  }, [signedIn, userLoading, sessionData?.authenticated, sessionLoading, sessionError, refetch])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchManager.resetRefetch(refetch)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refetch])

  const simplifiedStats = useMemo(() => {
    const activities = data?.stats?.allTime?.activities

    const obj = {
      census:
        (activities?.['EnhanceImageRecords-1950 Census Header Correction'] || 0) +
        (activities?.['EnhanceImageRecords-1950 Census Household Correction'] || 0) +
        (activities?.['EnhanceImageRecords-1950 Census Name Correction'] || 0),
      totalQuickNameReviews: activities?.['EnhanceImageRecords-Name Correction.reviewed'] || 0,
      totalFullNameReviews: activities?.['EnhanceImageRecords-Full Name Review.reviewed'] || 0,
      totalFamilyReviews: activities?.['EnhanceImageRecords-Family Review.reviewed'] || 0,
      totalRecordHints: activities?.['CuratePublicTree-Record Hint'] || 0,
      totalVerifyPlaces:
        (activities?.['CuratePublicTree-Verify Places'] || 0) +
        (activities?.['CuratePublicTree-Verify Places.submitted'] || 0) +
        (activities?.['CuratePublicTree-Verify Places.not-a-place'] || 0),
      totalRecordsIndexed: activities?.['IndexIDXRecords-INDEXING.INDEXING'] || 0,
      totalIndexingRecordsReviewed:
        (activities?.['IndexIDXRecords-INDEXING.REVIEW'] || 0) +
        (activities?.['IndexIDXRecords-INDEXING.ARBITRATE'] || 0) +
        (activities?.['IndexIDXRecords-INDEXING.ANALYZE'] || 0),
      totalIndexingRecords:
        (activities?.['IndexIDXRecords-INDEXING.ANALYZE'] || 0) +
        (activities?.['IndexIDXRecords-INDEXING.REVIEW'] || 0) +
        (activities?.['IndexIDXRecords-INDEXING.ARBITRATE'] || 0) +
        (activities?.['IndexIDXRecords-INDEXING.INDEXING'] || 0),
    }
    obj.fullNameReview = obj.totalFullNameReviews
    obj.familyReview = obj.totalFamilyReviews
    obj.quickNameReview = obj.totalQuickNameReviews
    obj.recordHints = obj.totalRecordHints
    obj.indexing = obj.totalIndexingRecords
    obj.reviewed = obj.totalIndexingRecordsReviewed
    obj.verifyPlaces = obj.totalVerifyPlaces

    return obj
  }, [data])

  // console.log(`simplifiedStats`, simplifiedStats)

  return { data, error, loading: isLoading, recalculate: refetch, simplifiedStats }
}
